import { useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  completePasswordReset,
  describePasswordReset,
  type Me,
  type ResetSummary,
} from '../api.js';

import { BrandMark, PRODUCT_LINE } from '../brand.js';

/**
 * Setting a new password from an admin-issued link. The second screen that
 * works without a session, and the only one that has to work for somebody who
 * is locked out.
 *
 * The email field is EMPTY and not pre-filled, exactly as on the invitation
 * screen and for exactly the same reason: the address is what binds the link to
 * a person, so printing it here would hand it to whoever the link reached by
 * accident. Anyone can be sent a link; only the right person knows whose it is.
 *
 * IT WARNS SOMEBODY WHO IS ALREADY SIGNED IN, for the same reason the invitation
 * screen does: finishing a reset puts you STRAIGHT INTO A SESSION as the person
 * whose link it is — deliberately, because somebody who has just proved a link
 * and chosen a password should not be made to type it again. The cookie belongs
 * to the browser, not the tab, so the account that was there is gone from every
 * tab at once. That caution went on the invitation screen and was left off this
 * one, which is the same trap with a different door.
 *
 * It matters MORE here. An admin who has just issued somebody's reset link is
 * the most likely person to click it, and they are signed in when they do.
 */

interface Props {
  readonly token: string;
  readonly onReset: () => void;
  /** Whoever this browser is currently signed in as, or null. */
  readonly signedInAs: Me | null;
}

type State =
  | { readonly status: 'checking' }
  | { readonly status: 'ready'; readonly reset: ResetSummary }
  | { readonly status: 'dead' };

export function ResetPassword({ token, onReset, signedInAs }: Props): JSX.Element {
  const [state, setState] = useState<State>({ status: 'checking' });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const reset = await describePasswordReset(token);
        if (!cancelled) setState({ status: 'ready', reset });
      } catch {
        // Used, cancelled, expired and never-existed all look the same here,
        // because the server deliberately does not tell them apart.
        if (!cancelled) setState({ status: 'dead' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await completePasswordReset({ token, email: email.trim(), password });
      onReset();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not set the new password.');
      setBusy(false);
    }
  }

  if (state.status === 'checking') {
    return (
      <main className="auth-shell">
        <p aria-live="polite">Checking the link…</p>
      </main>
    );
  }

  if (state.status === 'dead') {
    return (
      <main className="auth-shell">
        <div className="card">
          <h1 className="brand">
          <BrandMark size={30} />
          {PRODUCT_LINE}
        </h1>
          <p className="brand-sub">This reset link no longer works</p>
          <p>
            It may have already been used, been cancelled, or run out of time. Ask your
            administrator for a new one.
          </p>
          <div className="button-row">
            <a href="/">Go to sign in</a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-shell">
      <form className="card" onSubmit={submit} noValidate>
        <h1 className="brand">
          <BrandMark size={30} />
          {PRODUCT_LINE}
        </h1>
        <p className="brand-sub">Set a new password for {state.reset.company_name}</p>

        {error && (
          <div className="alert" role="alert">
            <span>{error}</span>
          </div>
        )}

        {signedInAs && (
          <div className="alert is-caution" role="status">
            <span>
              This browser is already signed in
              {signedInAs.company_name ? ` to ${signedInAs.company_name}` : ''}. Setting this
              password signs that account out and signs you in as whoever this link belongs to —
              in every tab, not just this one. To keep both, open this link in a private window
              instead.
            </span>
          </div>
        )}

        <div className="field">
          <label htmlFor="reset-email">Your email address</label>
          <input
            id="reset-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={320}
            disabled={busy}
            autoComplete="email"
          />
          <p className="hint">The address you sign in with.</p>
        </div>

        <div className="field">
          <label htmlFor="reset-password">Choose a new password</label>
          <input
            id="reset-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            maxLength={1024}
            disabled={busy}
            autoComplete="new-password"
          />
          <p className="hint">
            At least 12 characters. A short sentence you will remember is better than a short
            password you will not.
          </p>
        </div>

        {/* Said before they click, not after. Being signed out of the phone in
            your pocket is a surprise worth one sentence. */}
        <p className="hint">
          This also signs you out everywhere else, on every device.
        </p>

        <button type="submit" disabled={busy || !email.trim() || !password}>
          {busy ? 'Saving…' : 'Set new password'}
        </button>
      </form>
    </main>
  );
}
